import { useCallback, useContext, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthHandlerContext } from '../../../App'
import { REFRESH_TOKEN as REFRESH_TOKEN_KEY } from '../../../assets/constants'
import { isPinEnabledFlag, setPinUnlockPending } from '../../../util/pinBrowserFlags'
import ROUTES from '../../../assets/routes'
import { refreshAccessToken } from '../../../services/auth/tokenRefresh'
import { isAccessTokenValid } from '../../../util/authentication'
import { isLoginSessionWindowValid } from '../../../util/loginSessionWindow'

/**
 * 24-hour session window: when it expires and the user has a PIN, we refresh tokens and send them
 * to `/login` with pin-unlock pending (session + local storage) for a full-screen PIN flow in `RightGraphics`.
 * When the window expires and there is no PIN, we sign out (normal email/password login).
 */
export default function PinSessionUnlockGate({ children }: { children: React.ReactNode }) {
	const navigate = useNavigate()
	const { onLoggedOut } = useContext(AuthHandlerContext)

	const navigatingRef = useRef(false)

	const evaluate = useCallback(async () => {
		const hasRefresh = !!localStorage.getItem(REFRESH_TOKEN_KEY)
		if (!hasRefresh) {
			return
		}

		if (isLoginSessionWindowValid()) {
			return
		}

		if (navigatingRef.current) return
		const ok = await refreshAccessToken()
		if (!ok) {
			navigate(ROUTES.login, { replace: true })
			return
		}
		if (!isAccessTokenValid()) {
			navigate(ROUTES.login, { replace: true })
			return
		}

		const pinOn = isPinEnabledFlag()
		if (!pinOn) {
			onLoggedOut()
			return
		}

		navigatingRef.current = true
		try {
			setPinUnlockPending()
			navigate(ROUTES.login, { replace: true })
		} finally {
			navigatingRef.current = false
		}
	}, [navigate, onLoggedOut])

	useEffect(() => {
		void evaluate()
	}, [evaluate])

	useEffect(() => {
		const onVisibility = () => {
			if (document.visibilityState !== 'visible') return
			void evaluate()
		}
		document.addEventListener('visibilitychange', onVisibility)
		return () => document.removeEventListener('visibilitychange', onVisibility)
	}, [evaluate])

	return <>{children}</>
}
